import {
    type UscSettingsActionsType,
    USC_SETTINGS_INIT,
    USC_SETTINGS_SET_SHOW_SIMILAR_CARS,
    USC_SETTINGS_OVERRIDE,
} from "../actions/UscSettingsActions";
import { type UscLabelType, uscLabels } from "../../utils/labels";
import type { FinanceOptionType } from "../../../../common-deprecated/types/CommonTypes";
import { type QuickSpecConfigType, UscEnv } from "../../../shared-logic/utils/uscUtils";
import type { AemEcoLabelSettingsType } from "../../../../common-deprecated/types/SettingsType";
import { type DfEquipmentCategoryCode, DfServiceHistoryCode } from "../../../shared-logic/utils/dfConstants";
import type { ExternalPriceLocationType } from "../../../../common-deprecated/utils/taxLocations";
import type {
    UscAspectRatioType,
    UscObjectFitType,
    UscCustomerPromiseBannerType,
} from "../../../shared-logic/types/UscCommonTypes";
import { PdpSection, UscPdpFeeBehaviour } from "../../utils/constants";
import { UscCtaTypeEnum } from "../../utils/ctaUtils";
import type { EcolabelTmefConfigType } from "../../utils/ecoCostsUtils";
import type { SpecCategoryType } from "../../utils/specUtils";

export type UscSettingsType = {
    initialized: boolean;
    labels: Record<UscLabelType, string>;
    enableExternalPrice: boolean;
    externalPriceConfig: { key: string; label: string; disclaimer?: string }[];
    taxLocations: ExternalPriceLocationType[];
    uscEnv: UscEnv;
    ecoLabels: AemEcoLabelSettingsType | null; // Will be rendered when available. No setting to enable/disable these as of yet.
    onRetailerSite: boolean; // Indicates if the PDP has a retailer page as parent (= we're on a retailer PDP)
    showLocation: boolean; // Shows the location at the top in the basket

    // Accessories
    enableAccessories: boolean;
    disableAccessoryPrice: boolean; // Disables both cash & monthly price
    disableAccessoryMonthlyPrice: boolean; // Disables monthly price
    enableCarDelivery: boolean;

    // PDP
    quickSpecsConfigs: QuickSpecConfigType[];
    pdpSections: PdpSection[];
    specCategories: SpecCategoryType[];
    disabledEquipmentCategories: DfEquipmentCategoryCode[]; // This setting is undocumented as it is a hacky way to fix 'dirty' data. It also involves manually entering DF codes so not user-friendly as well.
    ucProgramCustomerPromiseBanner: UscCustomerPromiseBannerType;
    nonUcProgramCustomerPromiseBanner: UscCustomerPromiseBannerType;
    privatelySoldCustomerPromiseBanner: UscCustomerPromiseBannerType;
    showSimilarCars: boolean;
    pdpFeeBehaviour: UscPdpFeeBehaviour;
    disableMonthInRegistrationSpec: boolean;
    showZeroCo2SpecValues: boolean;
    visibleVehicleHistoryEvents: DfServiceHistoryCode[];
    hideFinanceTogglePrices: boolean;
    showFinanceLabelsOnCash: boolean;
    enablePreviousNextCars: boolean;
    aspectRatio: UscAspectRatioType;
    ecolabelTmefConfig: EcolabelTmefConfigType;
    enableTDGEcolabel: boolean;
    objectFit: UscObjectFitType;
    enablePrivatelySoldVehicles: boolean;
    enableUcProgramBadges: boolean;
    enableServiceHistory: boolean;
    enableVehicleAggregations: boolean;
    vehicleAggregationsVersionCode: string;
    hideCombinedPowerOutput: boolean;

    // Extras
    showCustomerSupportBanner: boolean;

    // True means a shared 'combined' value is used and the value will be dynamically chosen (WLTP or NEDC)
    // False means both WLTP and NEDC are shown
    autoDetermineCo2Standard: boolean;
    // CTA's
    loadSaveGatewayUrl: string;
    landingPageUrl: string;
    localContactDealerFormUrl: string;
    localOnlineReservationUrl: string; // Replaces the checkout flow with a local reservation flow
    detailPageUrl: string;
    ctas: UscCtaTypeEnum[];
    carDeliveryInfoUrl: string;
    enableDynamicCtas: boolean; // enables dynamic CTA's based on dealer operations we receive from A2D
    dynamicCtasHideBuyCtaOnCash: boolean; // hides the buy CTA when the finance option is cash
    dynamicCtasHideReserveCtaOnMonthly: boolean; // hides the reserve CTA when the finance option is monthly
    hidePrintButton: boolean; // hides the print button in general
    showAdditionalCtasOnMobile: boolean; // Shows the additional ctas on mobile (print + save to my toyota button)

    // Finance/Insurance
    insuranceGatewayUrl: string;
    tmefGatewayUrl: string;
    enableFinance: boolean;
    enableInsurance: boolean;
    requireDefaultInsurance: boolean; // Used in addition of enableInsurance, see OR-7117,
    defaultFinanceOption: FinanceOptionType;
    financeModuleUrl: string; // Points to the Makolab finance calculator AMD module
    monthlyPaymentUrl: string; // Points to the Makolab monthly payment calculator endpoint
    financeLabelsUseBoldText: boolean; // Used in the monthly price disclaimer to determine the text variant
    enableExclVatMonthlyPrice: boolean; // Enables the monthly price for exclVat vehicles
    insuranceIsYearly: boolean; // Used to determine if the insurance price is yearly or monthly

    // Retail/Upsell
    reservationInfoUrl: string; // This could currently be used inside the buy and reserve flow. In this future we might need to split this up into specific buy and reserve settings
    enableBuy: boolean;
    enableReserve: boolean;
    enableLocalReservation: boolean;
    openCheckoutStepsByDefault: boolean;

    // Dealer info
    showDealerPhoneNumber: boolean;
    showDealerEmail: boolean;
    showDealerWebsite: boolean;
    showDealerImpressum: boolean;

    // ⚠️ A temporary label was added for a smoother rollout, so the NMSC could have a different monthlyPriceFormat between v1 and v2
    // Now the rollout to v2 is finished the label should not be used anymore
    // The customer promise AEM updates is currently blocking us from updating all the FM in AEM
    // So added a temporary setting next to the label, so we only overwrite it in case this setting is enabled and the label is configured
    // TODO: Once this is false for all NMSCS we can remove this setting together with the label 'uscV2MonthlyPriceFormat'
    useV2MonthlyPriceFormatOverride: boolean;
};

export const initialStateUscSettings: UscSettingsType = {
    initialized: false,
    labels: { ...uscLabels },
    enableExternalPrice: false,
    externalPriceConfig: [], // Even though this is called externalPriceConfig it is more broadly used for broader 'feeList' localisation.
    taxLocations: [],
    uscEnv: UscEnv.Production,
    ecoLabels: null,
    onRetailerSite: false,
    showLocation: true,

    // Accessories
    enableAccessories: false,
    disableAccessoryPrice: false,
    disableAccessoryMonthlyPrice: false,
    enableCarDelivery: false,

    // PDP
    quickSpecsConfigs: [],
    pdpSections: [
        PdpSection.Highlights,
        PdpSection.Services,
        PdpSection.Accessories,
        PdpSection.CarDetails,
        PdpSection.DealerInfo,
    ],
    specCategories: [],
    disabledEquipmentCategories: [],
    ucProgramCustomerPromiseBanner: { title: "", bannerItems: [] },
    nonUcProgramCustomerPromiseBanner: { title: "", bannerItems: [] },
    privatelySoldCustomerPromiseBanner: { title: "", bannerItems: [] },
    showSimilarCars: false,
    pdpFeeBehaviour: UscPdpFeeBehaviour.Visited, // Visited by default as this was the base requirement for pilot TFR.
    disableMonthInRegistrationSpec: false,
    showZeroCo2SpecValues: false,
    visibleVehicleHistoryEvents: [
        DfServiceHistoryCode.HHC,
        DfServiceHistoryCode.RCS,
        DfServiceHistoryCode.RPR,
        DfServiceHistoryCode.Maintenance,
        DfServiceHistoryCode.VehicleDelivery,
    ],
    hideFinanceTogglePrices: false,
    showFinanceLabelsOnCash: false,
    autoDetermineCo2Standard: true,
    carDeliveryInfoUrl: "",
    loadSaveGatewayUrl: "",
    landingPageUrl: "",
    localContactDealerFormUrl: "",
    localOnlineReservationUrl: "",
    openCheckoutStepsByDefault: false,
    detailPageUrl: "",
    enablePreviousNextCars: false, // Disabled for now. Will be implemented in UC-43
    aspectRatio: "16:9",
    ecolabelTmefConfig: {},
    enableTDGEcolabel: false,
    objectFit: "cover",
    enablePrivatelySoldVehicles: false,
    enableUcProgramBadges: false,
    enableServiceHistory: true,
    enableVehicleAggregations: false,
    vehicleAggregationsVersionCode: "",
    hideCombinedPowerOutput: false,

    // Extras
    showCustomerSupportBanner: true,

    // CTA's
    ctas: [UscCtaTypeEnum.Buy, UscCtaTypeEnum.Reserve, UscCtaTypeEnum.ContactDealer, UscCtaTypeEnum.CallDealer],
    enableDynamicCtas: false,
    dynamicCtasHideBuyCtaOnCash: false,
    dynamicCtasHideReserveCtaOnMonthly: false,
    hidePrintButton: false,
    showAdditionalCtasOnMobile: true,

    // Finance/Insurance
    insuranceGatewayUrl: "",
    tmefGatewayUrl: "",
    // Finance/insurance will be enabled when the enableFinance/enableInsurance setting + the FM enableFinance/enableInsurance is set to true
    // The settings are currently mostly used for enabling the finance/insurance easier on the author environment
    // We set this setting default to true, so the default behaviour is that the FM determines whether the finance is shown or not
    enableFinance: true,
    enableInsurance: true,
    requireDefaultInsurance: true,
    defaultFinanceOption: "cash",
    financeModuleUrl: "",
    monthlyPaymentUrl: "",
    financeLabelsUseBoldText: false,
    enableExclVatMonthlyPrice: false,
    insuranceIsYearly: false,

    // Retail/Upsell
    reservationInfoUrl: "",
    enableBuy: false,
    enableReserve: false,
    enableLocalReservation: false,

    // Dealer info
    showDealerPhoneNumber: true,
    showDealerEmail: true,
    showDealerWebsite: true,
    showDealerImpressum: true,
    useV2MonthlyPriceFormatOverride: false,
};

const UscSettingsReducer = (
    state: UscSettingsType = initialStateUscSettings,
    action: UscSettingsActionsType,
): UscSettingsType => {
    switch (action.type) {
        case USC_SETTINGS_INIT: {
            return {
                ...state,
                ...action.initState,
                initialized: true,
            };
        }

        case USC_SETTINGS_SET_SHOW_SIMILAR_CARS: {
            return {
                ...state,
                showSimilarCars: action.show,
            };
        }

        case USC_SETTINGS_OVERRIDE: {
            return {
                ...state,
                ...action.override,
            };
        }

        default:
            return state;
    }
};

export default UscSettingsReducer;
